import React, { FC, PropsWithChildren, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  title: string;
  onClose?: () => void;
  /** Visually hide the heading (still announced). */
  hideTitle?: boolean;
  suit?: 'spades' | 'hearts' | 'diamonds' | 'clubs';
}

/**
 * Small accessible dialog: focus moves in, Tab is kept inside, Escape closes,
 * focus returns to the opener on close.
 */
export const Modal: FC<PropsWithChildren<ModalProps>> = ({
  title,
  onClose,
  hideTitle,
  suit = 'spades',
  children,
}) => {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusables = () =>
      Array.from(
        node?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      );
    (focusables()[0] || node)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onClose) {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="st-overlay"
      onClick={e => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div
        ref={ref}
        className="st-card st-stack"
        data-suit={suit}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        tabIndex={-1}
      >
        <h2 id={`${id}-title`} className={hideTitle ? 'st-sr-only' : 'st-h2'}>
          {title}
        </h2>
        {children}
      </div>
    </div>,
    document.body
  );
};
